/**
 * Where to send someone after they sign in, when they were on their way somewhere.
 *
 * ProtectedRoute has always handed the login page the location it turned away
 * (`state.from`), and the login page never read it: everyone landed on their role's default
 * page. The integration guide's first step links to Settings > API tokens, so a teammate who
 * was not signed in, the very person that page is for, signed in and found themselves on
 * Projects or Home with no token page in sight. Every deep link in an email (a task, its
 * comments) was lost the same way.
 *
 * Two sources, in order: the router state ProtectedRoute sets, and a `?next=` query that a
 * plain link can carry (the integration guide's Sign in buttons use it, and it survives a
 * reload of the login page, which router state does not).
 *
 * Only paths inside the portal are honoured. Anything else (another site, the login page
 * itself, a bare "/portal") falls back to the role's default, so neither source can be used
 * to bounce someone off the app.
 */
const PORTAL_PREFIX = '/portal/';

export function safePortalPath(raw: unknown): string | null {
  if (typeof raw !== 'string' || !raw.startsWith(PORTAL_PREFIX)) return null;
  // "/portal//evil.example" and "/portal/\evil.example" are still same-origin for the router,
  // but no real portal path contains either, so refuse rather than reason about parsers.
  if (raw.includes('//') || raw.includes('\\')) return null;
  return raw;
}

interface FromLocation { pathname?: unknown; search?: unknown; hash?: unknown }

export function returnPathAfterSignIn(state: unknown, search: string): string | null {
  const from = (state as { from?: FromLocation } | null | undefined)?.from;
  if (from && typeof from.pathname === 'string') {
    // Query and fragment are part of where they were going: ?person=all on the timesheet,
    // #comments on a task linked from a mention email.
    const query = typeof from.search === 'string' ? from.search : '';
    const fragment = typeof from.hash === 'string' ? from.hash : '';
    const fromState = safePortalPath(from.pathname + query + fragment);
    if (fromState) return fromState;
  }
  return safePortalPath(new URLSearchParams(search).get('next'));
}

interface PageLocation { pathname: string; search: string; hash: string }

/**
 * The login page to send someone to when their session has run out mid-visit.
 *
 * A session that has expired never meets ProtectedRoute's redirect: the stored tokens make
 * the app look signed in until the first request comes back 401 and the refresh fails, and
 * the API layer then does a full page load of the login page. That load carries no router
 * state, so the task an email linked to (with its #comments) was lost and people landed on
 * Projects or Home. It carries `?next=` instead, which `returnPathAfterSignIn` reads.
 *
 * Only a portal page is worth returning to; anywhere else gets the plain login page.
 */
export function loginUrlAfterSessionExpired(location: PageLocation): string {
  const here = safePortalPath(location.pathname + location.search + location.hash);
  return here ? `/login?next=${encodeURIComponent(here)}` : '/login';
}
